'use client'

import Autoplay from 'embla-carousel-autoplay'
import { Star } from 'lucide-react'
import React, { useEffect, useState } from 'react'

import { PartnersCarouselSection } from '@/components/Partners/PartnersCarouselSection'
import { ColorTrailText } from '@/components/ui/color-trail-text'
import {
  Carousel,
  type CarouselApi,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '@/components/ui/carousel'
import type { TestimonialCardData } from '@/lib/testimonials/testimonialCard'
import { localizePath } from '@/lib/i18n/config'
import { useLocale } from '@/lib/i18n/useLocale'
import { useMessages } from '@/lib/i18n/useMessages'
import { cn } from '@/utilities/ui'

type TestimonialCardProps = {
  author: string
  isBlurred?: boolean
  quote: string
  rating: number
}

const VISIBLE_CARD_COUNT = 5
const EDGE_OFFSET = Math.floor(VISIBLE_CARD_COUNT / 2)

function getOffsetFromActive(index: number, activeIndex: number, total: number): number {
  let diff = index - activeIndex

  if (diff > total / 2) diff -= total
  if (diff < -total / 2) diff += total

  return diff
}

function isEdgeCard(index: number, activeIndex: number, total: number): boolean {
  return Math.abs(getOffsetFromActive(index, activeIndex, total)) === EDGE_OFFSET
}

function StarRating({ rating }: { rating: number }) {
  const clampedRating = Math.min(5, Math.max(1, Math.round(rating)))

  return (
    <div
      aria-label={`${clampedRating} out of 5 stars`}
      className="flex items-center gap-[1.33px]"
      role="img"
    >
      {Array.from({ length: 5 }).map((_, index) => (
        <Star
          key={index}
          aria-hidden
          className={cn(
            'size-4',
            index < clampedRating ? 'fill-star text-star' : 'fill-transparent text-dark-800/20',
          )}
        />
      ))}
    </div>
  )
}

function TestimonialCard({ author, isBlurred, quote, rating }: TestimonialCardProps) {
  return (
    <article
      className={cn(
        'flex h-[24rem] w-full flex-col gap-6 rounded-xl border border-black/10 bg-white/50 px-6 py-6 transition-[filter] duration-300 lg:px-8',
        isBlurred && 'blur-[5px]',
      )}
    >
      <StarRating rating={rating} />
      <blockquote className="flex flex-1 flex-col">
        <p className="font-outfit text-sm leading-5 text-dark-800/80">{quote}</p>
      </blockquote>
      <footer>
        <cite className="font-outfit text-base font-medium leading-6 text-dark-800/80 not-italic">
          {author}
        </cite>
      </footer>
    </article>
  )
}

type TestimonialsSectionProps = {
  /** When false, hide the partner-logos block under the carousel (e.g. Partners page already has PartnersCarouselSection). */
  showPartnerLogos?: boolean
  testimonials: TestimonialCardData[]
}

export default function TestimonialsSection({
  showPartnerLogos = true,
  testimonials,
}: TestimonialsSectionProps) {
  const messages = useMessages()
  const locale = useLocale()
  const copy = messages.landing.studentTestimonials
  const [api, setApi] = useState<CarouselApi>()
  const [activeIndex, setActiveIndex] = useState(0)

  const autoplayPlugin = React.useRef(
    Autoplay({ delay: 5000, stopOnInteraction: true, stopOnMouseEnter: true }),
  )

  useEffect(() => {
    if (!api) return

    const onSelect = () => {
      setActiveIndex(api.selectedScrollSnap())
    }

    onSelect()
    api.on('select', onSelect)
    api.on('reInit', onSelect)

    return () => {
      api.off('select', onSelect)
      api.off('reInit', onSelect)
    }
  }, [api])

  const showCarousel = testimonials.length > 0

  return (
    <section
      aria-labelledby="student-testimonials-heading"
      className="w-full bg-cream"
      id="partners"
    >
      <div className={cn('w-full max-w-none py-12 lg:py-20')}>
        <ColorTrailText
          as="h2"
          className="text-title text-center font-semibold text-bluetext section-container"
          id="student-testimonials-heading"
          segments={[
            {
              finalColor: 'var(--brand-bluetext)',
              text: copy.title,
            },
          ]}
        />

        {showCarousel ? (
          <Carousel
            aria-label={copy.carouselAriaLabel}
            className="relative mt-10 w-full lg:mt-12"
            opts={{
              align: 'center',
              loop: true,
              containScroll: false,
            }}
            plugins={[autoplayPlugin.current]}
            setApi={setApi}
          >
            <CarouselPrevious
              aria-label={copy.scrollPrevious}
              className="top-1/2 left-1 z-10 size-10 -translate-y-1/2 rounded-full border-[1.5px] border-white/95 bg-black/50 text-white hover:bg-black/60 hover:text-white disabled:opacity-40 lg:left-0 lg:ml-8"
              size="icon"
              variant="outline"
            />
            <CarouselNext
              aria-label={copy.scrollNext}
              className="top-1/2 right-1 z-10 size-10 -translate-y-1/2 rounded-full border-[1.5px] border-white/95 bg-black/50 text-white hover:bg-black/60 hover:text-white disabled:opacity-40 lg:right-0 lg:mr-8"
              size="icon"
              variant="outline"
            />

            <CarouselContent className="-ml-2">
              {testimonials.map((testimonial, index) => {
                const isBlurred = isEdgeCard(index, activeIndex, testimonials.length)

                return (
                  <CarouselItem
                    key={testimonial.id}
                    className="basis-[min(85vw,400px)] pl-2 lg:basis-1/4"
                  >
                    <TestimonialCard
                      author={testimonial.author}
                      isBlurred={isBlurred}
                      quote={testimonial.quote}
                      rating={testimonial.rating}
                    />
                  </CarouselItem>
                )
              })}
            </CarouselContent>
          </Carousel>
        ) : null}

        {showPartnerLogos ? (
          <PartnersCarouselSection
            className={cn(showCarousel ? 'mt-20 lg:mt-28' : 'mt-10 lg:mt-12')}
            learnMoreHref={localizePath('/partners', locale)}
            learnMoreLabel={copy.learnMore}
            locale={locale}
          />
        ) : null}
      </div>
    </section>
  )
}
